import { useMemo, useRef, useEffect } from "react";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { scoreColor } from "@/lib/brand";
import type { Task } from "@/types/dashboard";

interface TaskListProps {
  tasks: Record<string, Task>;
  onTaskClick?: (questionId: string) => void;
  totalQuestions?: number;
}

export function TaskList({ tasks, onTaskClick }: TaskListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const sortedTasks = useMemo(() => {
    const taskArray = tasks ? Object.values(tasks) : [];

    // Simply return sorted tasks without generating placeholders
    // The backend should send all tasks (including queued ones)
    return taskArray.sort((a, b) => {
      // Extract question number and run number for proper sorting
      // Format: q001_finance_run1, q002_hr_run2, etc.
      const parseId = (id: string) => {
        const match = id.match(/q(\d+).*_run(\d+)/);
        if (match) {
          return {
            question: parseInt(match[1], 10),
            run: parseInt(match[2], 10)
          };
        }
        return { question: 0, run: 0 };
      };

      const parsedA = parseId(a.question_id);
      const parsedB = parseId(b.question_id);

      // Sort by question number first, then by run number
      if (parsedA.question !== parsedB.question) {
        return parsedA.question - parsedB.question;
      }
      return parsedA.run - parsedB.run;
    });
  }, [tasks]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [sortedTasks.length]);

  if (sortedTasks.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Clock className="h-5 w-5" />
          </div>
          <p className="font-display text-sm font-semibold">Nog geen vragen</p>
          <p className="text-sm text-muted-foreground">Vragen verschijnen hier zodra de run start.</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={listRef} className="h-full space-y-2 overflow-y-auto pr-1">
      {sortedTasks.map((task) => (
        <TaskItem key={task.question_id} task={task} onClick={onTaskClick} />
      ))}
    </div>
  );
}

type StatusConfig = {
  icon: typeof Clock;
  rowClass: string;
  iconClass: string;
  pillClass: string;
  label: string;
};

const QUEUED: StatusConfig = {
  icon: Clock,
  rowClass: "border-border bg-card",
  iconClass: "text-muted-foreground",
  pillClass: "bg-sl-grey-100 text-muted-foreground border-sl-grey-300",
  label: "In wachtrij",
};

function TaskItem({ task, onClick }: { task: Task; onClick?: (questionId: string) => void }) {
  const isPlaceholder = task.question === 'Waiting to start...';

  const statusConfig = useMemo<StatusConfig>(() => {
    // Check if this is a placeholder (not yet started)
    if (isPlaceholder) {
      return { ...QUEUED, label: "Niet gestart" };
    }

    switch (task.status) {
      case "queued":
        return QUEUED;
      case "running":
        return {
          icon: Loader2,
          rowClass: "border-sl-yellow bg-sl-yellow-soft",
          iconClass: "text-sl-charcoal",
          pillClass: "bg-sl-yellow text-sl-charcoal border-sl-yellow",
          label: "Bezig",
        };
      case "completed":
      case "success":
        return {
          icon: CheckCircle2,
          rowClass: "border-border bg-card",
          iconClass: "text-success",
          pillClass: "bg-[#EEF6E4] text-success border-[#CFE3B4]",
          label: "Voltooid",
        };
      case "failed":
      case "error":
        return {
          icon: XCircle,
          rowClass: "border-[#F6C4C6] bg-[#FFF7F7]",
          iconClass: "text-destructive",
          pillClass: "bg-[#FDECEC] text-destructive border-[#F6C4C6]",
          label: "Mislukt",
        };
      default:
        return { ...QUEUED, label: (task.status as string) || "Onbekend" };
    }
  }, [task.status, isPlaceholder]);

  const Icon = statusConfig.icon;

  const isClickable = !isPlaceholder && (task.status === "running" || task.status === "completed" || task.status === "success" || task.status === "failed" || task.status === "error") && onClick;

  return (
    <div
      className={cn(
        "group flex items-start gap-3 rounded-md border p-3 transition-colors",
        statusConfig.rowClass,
        isClickable && "cursor-pointer hover:border-sl-charcoal/40 hover:shadow-card",
      )}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={() => isClickable && onClick(task.question_id)}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick(task.question_id);
        }
      }}
    >
      <div className={cn("mt-0.5 shrink-0", statusConfig.iconClass)}>
        <Icon className={cn("h-4 w-4", task.status === "running" && "animate-spin")} />
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className={cn("rounded-full border px-2 py-0.5 text-xs font-medium", statusConfig.pillClass)}>
              {statusConfig.label}
            </span>
            <span className="truncate font-mono text-xs text-muted-foreground">{task.question_id}</span>
            {task.status === "running" && task.server_idx !== undefined && (
              <span className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                server {task.server_idx}
              </span>
            )}
          </div>

          {(task.status === "completed" || task.status === "success") && task.accuracy_score !== undefined && (
            <div
              className="flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-semibold tabular-nums text-white"
              style={{ backgroundColor: scoreColor(task.accuracy_score) }}
              title={task.sql_valid ? "Score (SQL geldig)" : "Score"}
            >
              <span>{task.accuracy_score?.toFixed(1) ?? 'n.v.t.'}</span>
              <span className="text-xs opacity-80">/5</span>
              {task.sql_valid && <span className="text-xs">✓</span>}
            </div>
          )}
        </div>

        <p className="line-clamp-2 text-sm leading-relaxed text-foreground">{task.question}</p>

        {task.status === "failed" && task.error && (
          <p className="line-clamp-1 text-xs font-medium text-destructive" title={task.error}>
            ⚠ {task.error}
          </p>
        )}
      </div>
    </div>
  );
}
